import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
  } from 'react';
  import { checkApiSuccess, getApiData } from '@common/utils';
  import {
    GridFormBox,
    GridFormItem,
    GridGuide,
    GridInput,
    GridTable,
  } from '@components/common/grid';
  import { SIMULATION_CALC_TYPE } from '@common/utils/simulation';
  
  const SimulationAnnuityInAdvance = forwardRef(({ simulationInfo = {} }, ref) => {
    const { verifyPayload = {}, productInfo = {} } = simulationInfo;
  
    const { error: toastError } = $hooks.useToast();
    const { calcSimulation } = $hooks.useApi();
  
    const [age, setAge] = useState('');
    const [ageOptions, setAgeOptions] = useState([]);
    const [simulationData, setSimulationData] = useState(null);
  
    const [isLoaded, setIsLoaded] = useState(false);
  
    const setData = () => {
      const age = verifyPayload.insuranceAge;
      const startAge = Math.max(age + 10, 60),
        endAge = 80;
      const ageOptions = Array.from({
        length: endAge - startAge + 1,
      }).map((_, i) => ({
        label: `${startAge + i}세`,
        value: startAge + i,
      }));
      setAgeOptions(ageOptions);
      setAge(ageOptions[0]?.value);
  
      setIsLoaded(true);
    };
  
    const calculate = async () => {
      try {
        const simulType = SIMULATION_CALC_TYPE.annuityInAdvance;
  
        const {
          calculateDate,
          genderCode,
          insuranceAge,
          payinMethod,
          planEstablishProduct,
        } = verifyPayload;
        const { productCode } = productInfo?.productDetail ?? {};
  
        const payload = {
          prmumCalcBasDd: calculateDate,
          saleProdCd: productCode.substring(0, 6),
          gndrCd: genderCode,
          riskGrdCd: 3,
          insrnAge: insuranceAge,
          anutyUpfrntBgnAge: age,
          payinFrqcyCd: payinMethod,
          planEstablishProductDTO: planEstablishProduct.map((item, idx) => {
            return {
              riderNo: idx + 1,
              prodCd: item.productCode,
              cvrgPdTpCd: item.coveragePeriodTypeCode,
              cvrgPd: Number(item.coveragePeriodValue),
              payinPdTpCd: item.payinPeriodTypeCode,
              payinPd: item.payinPeriodValue,
              // anutyBgnAge: Number(item.oldIncomeAge),
              faceamnt: item.faceAmount * item.faceAmountUnit,
              calPrmum: item.premium,
            };
          }),
        };
  
        const response = await calcSimulation({ simulType, payload });
        const isSuccess = checkApiSuccess(response);
        if (!isSuccess) throw new Error('시뮬레이션 계산 API 실패');
  
        const data = getApiData(response);
  
        setSimulationData(data);
      } catch (error) {
        console.log('calculate() - error', error);
        toastError('시뮬레이션 계산에 실패하였습니다.');
      }
    };
  
    useEffect(setData, []);
    useEffect(() => {
      if (isLoaded) calculate();
    }, [isLoaded]);
  
    useImperativeHandle(ref, () => ({
      calc: () => {
        if (isLoaded) calculate();
      },
      // NOTE: 계산하기 버튼 무조건 활성화 시키고, 클릭 시 이 컴포넌트에서 체크
      checkValid: () => true,
    }));
  
    return (
      <>
        <GridFormBox>
          <GridFormItem label="연금선지급개시나이">
            <GridInput
              border
              type="select"
              width="150px"
              options={ageOptions}
              value={age}
              onChange={value => setAge(value)}
            />
          </GridFormItem>
        </GridFormBox>
  
        <GridGuide
          guides={[
            `연금선지급금은 '선지급개시 직전 보험가입금액의 5%'를 말합니다.`,
            '연금선지급금은 신청일을 포함하여 연금선지급기간(10년)동안 매년 연금 선지급 신청 해당일에 지급됩니다. 단, 해당월에 연금 선지급 신청 해당일이 없는 경우에는 해당 월의 말일에 연금 선지급금을 지급합니다.',
            '연금선지급 후 주계약가입금액은 아래에서 정의한 선지급비율만큼 감액됩니다.',
            '선지급비율=(연금선지급금)÷(연금선지급 직전 해약환급금)',
            '연금선지급 후 주계약가입금액=연금선지급 직전 가입금액×(1-선지급비율)',
            '연금선지급은 60세~80세 사이(단, 가입일로부터 10년이상 경과된 이후)에 신청할 수 있습니다.',
            '연금선지급기간 중 계약자의 신청에 따라 연금선지급 중지가 가능하며,이 경우 가입금액의 자동감액을 중지하고 더 이상 연금선지급금을 지급하지 않습니다. 또한, 계약자가 연금 선지급을 중지한 경우에는 다시 연금선지급을 신청할 수 없습니다.',
            '계약자는 연금선지급기간 중에는 연금선지급에 따른 가입금액의 감액 외의 감액을 신청할 수 없습니다.',
            '사망보험금의 연금선지급에 관한 자세한 사항은 약관 및 상품설명서를 참고하시기 바랍니다.',
          ]}
        />
  
        <GridTable
          id="grid-old-income"
          options={{
            data: simulationData?.anutyUpfrntSvrExmpDTO ?? [],
            colNames: [
              '경과년수',
              '도달나이',
              '납입보험료누계',
              '연금선지급금액',
              '사망보험금',
              '주계약 해약환급금',
              '환급률',
            ],
            colModel: [
              {
                name: 'progYycnt',
                formatter: value => `${value}년`,
                align: 'right',
              },
              {
                name: 'insrnAge',
                formatter: value => `${value}세`,
                align: 'right',
              },
              {
                name: 'premiumPaid',
                sortable: false,
                sorttype: 'int',
                formatter: 'integer',
                align: 'right',
              },
              {
                name: 'anutyUpfrntAmt',
                sortable: false,
                sorttype: 'int',
                formatter: 'integer',
                align: 'right',
              },
              {
                name: 'deathBnf',
                sortable: false,
                sorttype: 'int',
                formatter: 'integer',
                align: 'right',
              },
              {
                name: 'srndRefndAmnt',
                sortable: false,
                sorttype: 'int',
                formatter: 'integer',
                align: 'right',
              },
              {
                name: 'srndRefndRate',
                sortable: false,
                sorttype: 'int',
                formatter: value => `${value}%`,
                align: 'right',
              },
            ],
            formatter: { integer: { thousnadsSeparator: ',', defaultValue: 0 } },
            jsonReader: { repeatitems: false },
            multiselect: false,
            viewrecords: true,
          }}
        />
      </>
    );
  });
  
  export default SimulationAnnuityInAdvance;
  